import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Box, IconButton, InputAdornment, TextField } from '@mui/material'
import { useCallback, useState } from 'react'
import { useListContext, useTranslate } from 'react-admin'

/**
 * GlobalSearchBox - Multi-field fuzzy search component for Pets list
 *
 * Searches across:
 * - name (pet name)
 * - breed (pet breed)
 * - species (pet species)
 * - id (pet ID)
 * - owner.name (owner name)
 * - owner.email (owner email)
 * - owner.clinic_name (clinic name)
 *
 * Features:
 * - Case-insensitive fuzzy matching
 * - Multi-word AND logic (all terms must match)
 * - Phrase matching with quotes
 * - Enter key or search icon to trigger
 */
const GlobalSearchBox = () => {
  const { filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const [searchText, setSearchText] = useState(filterValues.q || '')

  const handleSearch = useCallback(() => {
    if (searchText.trim()) {
      setFilters({ ...filterValues, q: searchText.trim() }, {})
    } else {
      // Clear search filter if empty
      const { q, ...rest } = filterValues
      setFilters(rest, {})
    }
  }, [searchText, filterValues, setFilters])

  const handleClear = useCallback(() => {
    setSearchText('')
    const { q, ...rest } = filterValues
    setFilters(rest, {})
  }, [filterValues, setFilters])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch],
  )

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }, [])

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        value={searchText}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={translate('resources.pets.search_placeholder', {
          _: 'Search by Pet Name / Owner / Email / Clinic / Breed / ID',
        })}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchText && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                edge="end"
                aria-label={translate('pos.common.clear_search')}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          maxWidth: 600,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
          },
        }}
      />
    </Box>
  )
}

export default GlobalSearchBox
